import React from 'react';
import { motion } from 'motion/react';
import { ArrowRight, QrCode } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ParticlesBg } from '../components/layout/ParticlesBg';
import { Section, Eyebrow, Ridgeline, fadeInUp } from '../components/layout/Section';
import { WindowFrame, SpotlightCard, StatusPill } from '../components/layout/Tech';
import { ScrollTilt } from '../components/layout/Motion3D';

const arcadeVideoUrl = 'https://demo.arcade.software/video/80m4zsJCGTY3Ahh6Pshz?embed&autoplay=true&muted=true&mute=true&playsinline=true';
const englishArcadeVideoUrl = 'https://demo.arcade.software/video/yirMaAWGRtXzB8Sb7rzA?embed&autoplay=true&muted=true&mute=true&playsinline=true';
const marketingPageUrl = 'https://www.globistaan.com/marketing';
const marketingContact = '+91 8291486041';
const marketingEmail = 'ceo@globistaan.com';
const marketingQrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=320x320&margin=16&data=${encodeURIComponent(marketingPageUrl)}`;

const VideoFrame = ({ src, title, label }) => (
  <motion.div
    initial={{ opacity: 0, y: 28, scale: 0.98 }}
    whileInView={{ opacity: 1, y: 0, scale: 1 }}
    viewport={{ once: true }}
    transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
  >
    <ScrollTilt from={14} to={-8}>
    <WindowFrame url={`demo.arcade.software · ${label}`} right={<StatusPill status="Live" />}>
      <div className="relative" style={{ aspectRatio: '16 / 9', background: '#05070d' }}>
        <iframe
          src={src}
          title={title}
          className="absolute inset-0 w-full h-full border-0"
          allow="autoplay; fullscreen; clipboard-write; encrypted-media; picture-in-picture"
          allowFullScreen
          loading="eager"
        />
      </div>
    </WindowFrame>
    </ScrollTilt>
  </motion.div>
);

export default function Marketing() {
  return (
    <main>
      <Section
        tone="ink"
        pattern="grid"
        aurora
        topo
        watermark
        rule={false}
        padding="pt-36 pb-24"
        className="min-h-screen"
        glows={[
          { color: 'rgba(34,211,238,0.24)', top: '-8%', left: '-8%', size: 640 },
          { color: 'rgba(124,58,237,0.28)', top: '35%', right: '-10%', size: 640 },
          { color: 'rgba(52,211,153,0.14)', bottom: '-5%', left: '30%', size: 520 }
        ]}
        contentWidth={1180}
      >
        <ParticlesBg />

        <div className="relative z-10">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <div className="mb-6"><Eyebrow>Marketing Video</Eyebrow></div>
            <h1 className="h-display" style={{ fontSize: 'clamp(2.25rem, 5vw, 4rem)', marginBottom: '1.25rem' }}>
              Globistaan for Businesses and Talent
            </h1>
            <p className="lede mx-auto" style={{ maxWidth: '760px' }}>
              A quick walkthrough of how Globistaan connects practical technology delivery with business needs and skilled contributors.
            </p>
          </motion.div>

          <motion.div {...fadeInUp} className="mb-6 text-center">
            <div className="mb-4"><Eyebrow style={{ '--eyebrow-fg': '#FCD34D', '--eyebrow-border': 'rgba(251,191,36,0.4)', '--eyebrow-bg': 'rgba(251,191,36,0.1)' }}>हिंदी में देखें</Eyebrow></div>
            <h2 className="h-section" style={{ fontSize: 'clamp(1.55rem, 3.2vw, 2.35rem)', marginBottom: '0.75rem' }}>
              हिंदी परिचय
            </h2>
          </motion.div>

          <VideoFrame src={arcadeVideoUrl} title="Globistaan marketing video" label="हिंदी" />

          <motion.div {...fadeInUp} className="mt-14 mb-6 text-center">
            <div className="mb-4"><Eyebrow>Watch in English</Eyebrow></div>
            <h2 className="h-section" style={{ fontSize: 'clamp(1.55rem, 3.2vw, 2.35rem)', marginBottom: '0.75rem' }}>
              English Overview
            </h2>
          </motion.div>

          <VideoFrame src={englishArcadeVideoUrl} title="Globistaan marketing video in English" label="English" />

          <motion.div {...fadeInUp} className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/contact" className="btn-primary btn-accent text-xs" style={{ textDecoration: 'none', padding: '0.85rem 1.6rem' }}>
              Start a Conversation <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>

          <motion.div {...fadeInUp} className="mt-12 mx-auto" style={{ maxWidth: '760px' }}>
          <SpotlightCard accent="accent-cyan" style={{ '--card-pad': '1.5rem' }}>
          <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 items-center">
            <a
              href={marketingPageUrl}
              target="_blank"
              rel="noreferrer"
              className="block mx-auto"
              aria-label="Open Globistaan marketing page QR code link"
            >
              <img
                src={marketingQrUrl}
                alt="QR code for Globistaan marketing page"
                className="w-40 h-40 rounded-xl"
                style={{ background: '#ffffff', padding: '0.5rem' }}
              />
            </a>
            <div className="text-center md:text-left min-w-0">
              <p className="flex items-center justify-center md:justify-start gap-2" style={{ color: 'var(--text-primary)', fontSize: '1rem', fontWeight: 700, marginBottom: '0.5rem', fontFamily: 'var(--font-display)' }}>
                <QrCode className="w-4 h-4" style={{ color: '#22D3EE' }} /> Scan to open this marketing page
              </p>
              <a
                href={marketingPageUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-block no-underline"
                style={{ color: '#67E8F9', fontFamily: 'var(--font-mono)', fontSize: '0.9rem', wordBreak: 'break-all' }}
              >
                {marketingPageUrl}
              </a>
              <p style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)', fontSize: '0.88rem', marginTop: '0.65rem', marginBottom: 0 }}>
                Contact: {marketingContact}
              </p>
              <p style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)', fontSize: '0.88rem', marginTop: '0.35rem', marginBottom: 0 }}>
                Email: {marketingEmail}
              </p>
            </div>
          </div>
          </SpotlightCard>
          </motion.div>
        </div>
        <Ridgeline opacity={0.6} height={160} />
      </Section>
    </main>
  );
}
